import * as THREE from "three";

import "./styles/tokens.css";
import "./styles/shell.css";
import "./styles/parts.css";
import "./styles/basket.css";
import "./styles/modal.css";

import { loadCatalog } from "./catalog/loader";
import { PartsStore } from "./catalog/parts-store";
import { PartsList } from "./catalog/parts-list";

import { createScene, startRenderLoop } from "./viewer/scene";
import { loadModel } from "./viewer/loader";
import { Picker } from "./viewer/picking";
import { HoverIsolate } from "./viewer/hover-isolate";
import { ExplodeController } from "./viewer/explode-toggle";

import { Basket } from "./basket/store";
import { buildLayout } from "./shell/layout";
import { BasketFab } from "./shell/basket-fab";
import { BasketDrawer } from "./shell/basket-drawer";
import { AnfrageModal } from "./shell/anfrage-modal";
import { attachBottomSheet } from "./shell/bottom-sheet";

import { t } from "./i18n/t";

const TAP_MOVE_THRESHOLD = 8; // pixels — below this counts as a tap, above is drag

async function bootstrap() {
  const mount = document.getElementById("kb-spf");
  if (!mount) throw new Error("#kb-spf mount node not found");
  const bedId = mount.dataset.bed ?? "emma-1";
  const embedded = mount.dataset.embedded === "true";

  const catalog = await loadCatalog(bedId);
  document.title = `${t("app.title")} · ${catalog.name.de} · Kayser Betten`;

  const layout = buildLayout("kb-spf", catalog, embedded);

  const scene = createScene(layout.canvas);
  const model = await loadModel(catalog);
  scene.scene.add(model.root);

  // Frame the camera based on the model size
  scene.controls.target.copy(model.sceneCenter);
  const r = model.sceneRadius;
  scene.camera.position.set(
    model.sceneCenter.x + r * 1.6,
    model.sceneCenter.y + r * 0.6,
    model.sceneCenter.z + r * 1.7,
  );
  scene.camera.updateProjectionMatrix();
  scene.controls.update();

  const partsStore = new PartsStore();
  const basket = new Basket(catalog.id);

  const partsList = new PartsList(layout.partsPane, catalog, model, partsStore, basket);
  void partsList;
  attachBottomSheet(layout.partsPane);

  const hoverIsolate = new HoverIsolate(model);
  const exploder = new ExplodeController(model);
  const picker = new Picker(scene, model);

  // Wire parts-store → viewer
  partsStore.subscribe((s) => {
    hoverIsolate.setHovered(s.hoveredPart);
    hoverIsolate.setSelected(s.selectedPart);
  });

  // ── Pointer interaction on the canvas ────────────────────────────
  // Click reliability: hover-isolate moves the part toward the camera, so
  // re-picking at pointer-up can land on a *different* mesh than the user
  // intended. We instead record what the user pressed-on at pointer-down
  // and use that for selection (provided it stayed within tap threshold).
  let downX = 0,
    downY = 0,
    isPotentialTap = false,
    isOrbiting = false,
    downMesh: string | null = null;
  scene.controls.addEventListener("start", () => {
    isOrbiting = true;
  });
  scene.controls.addEventListener("end", () => {
    isOrbiting = false;
  });

  layout.canvas.addEventListener("pointerdown", (e) => {
    downX = e.clientX;
    downY = e.clientY;
    isPotentialTap = e.pointerType !== "mouse" || e.button === 0;
    // Capture the mesh BEFORE hover-isolate has a chance to nudge anything.
    downMesh = picker.pick(e.clientX, e.clientY);
  });

  layout.canvas.addEventListener("pointermove", (e) => {
    if (Math.hypot(e.clientX - downX, e.clientY - downY) > TAP_MOVE_THRESHOLD) {
      isPotentialTap = false;
    }

    // Hover only with mouse (touch devices don't get hover-isolate; tap-to-isolate)
    if (e.pointerType !== "mouse") return;
    if (isOrbiting) return;

    const meshName = picker.pick(e.clientX, e.clientY);
    partsStore.setHovered(meshName);
    if (meshName) {
      const part = catalog.parts.find((p) => p.meshName === meshName);
      if (part) showTooltip(layout, e.clientX, e.clientY, part.name.de, part.artNr);
      layout.canvas.style.cursor = "pointer";
    } else {
      hideTooltip(layout);
      layout.canvas.style.cursor = "";
    }
  });

  layout.canvas.addEventListener("pointerleave", () => {
    partsStore.setHovered(null);
    hideTooltip(layout);
    layout.canvas.style.cursor = "";
  });

  layout.canvas.addEventListener("pointerup", () => {
    if (!isPotentialTap) return;
    const meshName = downMesh;
    const cur = partsStore.get().selectedPart;
    if (meshName) {
      partsStore.setSelected(cur === meshName ? null : meshName);
    } else {
      partsStore.setSelected(null);
    }
    downMesh = null;
  });

  // ── Explosion controls ───────────────────────────────────────────
  layout.explodeToggle.addEventListener("click", () => {
    const on = !layout.explodeToggle.classList.contains("on");
    layout.explodeToggle.classList.toggle("on", on);
    layout.explodeToggle.setAttribute("aria-pressed", String(on));
    layout.controls.classList.toggle("expanded", on);
    if (on) {
      layout.explodeSlider.value = "100";
      layout.explodePct.textContent = "100 %";
      exploder.setTarget(1);
    } else {
      layout.explodeSlider.value = "0";
      layout.explodePct.textContent = "0 %";
      exploder.setTarget(0);
    }
  });

  layout.explodeSlider.addEventListener("input", () => {
    const v = Number(layout.explodeSlider.value);
    layout.explodePct.textContent = `${v} %`;
    const t = v / 100;
    exploder.setTarget(t);
    if (v > 0 && !layout.explodeToggle.classList.contains("on")) {
      layout.explodeToggle.classList.add("on");
      layout.controls.classList.add("expanded");
    }
    if (v === 0 && layout.explodeToggle.classList.contains("on")) {
      layout.explodeToggle.classList.remove("on");
    }
  });

  // ── Basket UI ────────────────────────────────────────────────────
  const drawer = new BasketDrawer(document.body, basket, () => modal.open());
  new BasketFab(document.body, basket, () => drawer.open());
  const modal = new AnfrageModal(
    document.body,
    catalog,
    basket,
    (msg) => showToast(layout, msg),
    () => {
      drawer.close();
    },
  );

  // ── Render loop ──────────────────────────────────────────────────
  const offsetBuffer = new Map<THREE.Mesh, THREE.Vector3>();
  startRenderLoop(scene, (dt) => {
    exploder.tick(dt, offsetBuffer);
    hoverIsolate.setExplodeOffset(offsetBuffer);
    hoverIsolate.tick(dt);
  });

  // Pause hover-isolate during orbit drag
  scene.controls.addEventListener("start", () => {
    partsStore.setHovered(null);
    hideTooltip(layout);
  });
  void isPotentialTap; // suppress unused-warning when only mouse path runs

  // Hide loading
  setTimeout(() => layout.loading.classList.add("hidden"), 280);
}



function showTooltip(layout: ReturnType<typeof buildLayout>, x: number, y: number, name: string, art: string) {
  const r = layout.canvas.getBoundingClientRect();
  layout.tooltip.innerHTML = `<strong>${escapeHtml(name)}</strong><span class="kb-tooltip-art">${art}</span>`;
  layout.tooltip.style.left = `${x - r.left + 14}px`;
  layout.tooltip.style.top = `${y - r.top - 14}px`;
  layout.tooltip.classList.add("visible");
}

function hideTooltip(layout: ReturnType<typeof buildLayout>) {
  layout.tooltip.classList.remove("visible");
}

let toastTimer: number | null = null;
function showToast(layout: ReturnType<typeof buildLayout>, message: string) {
  layout.toast.textContent = message;
  layout.toast.classList.add("visible");
  if (toastTimer !== null) clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => layout.toast.classList.remove("visible"), 3600);
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

bootstrap().catch((err) => {
  console.error(err);
  const root = document.getElementById("kb-spf");
  if (root) {
    root.innerHTML = `<div style="padding:48px;text-align:center;color:#3a3a3a;font-family:sans-serif"><h2>Fehler</h2><p>${String(err.message ?? err)}</p></div>`;
  }
});

